import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Bell, 
  X, 
  Check, 
  Swords, 
  Sparkles, 
  Trophy, 
  ChevronDown, 
  ChevronUp,
  BellRing
} from 'lucide-react';
import KSButton from '../common/KSButton';
import { useNotification } from '../../contexts/NotificationContext';

export default function NotificationSoftPromptModal() {
  const { showSoftPrompt, dismissSoftPrompt, enableWebPush } = useNotification();
  const [showPreferences, setShowPreferences] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [alertPreferences, setAlertPreferences] = useState({
    challenges: true,
    dailyPuzzles: true,
    tournaments: true,
  });

  if (!showSoftPrompt) return null;

  const handleEnable = async () => {
    try {
      setSubscribing(true);
      await enableWebPush();
    } catch (err) {
      console.warn('Failed to subscribe to web push:', err);
    } finally {
      setSubscribing(false);
    }
  };

  const handleDismiss = () => {
    dismissSoftPrompt();
  };

  return (
    <aside 
      aria-label="Push Notification Permission Prompt"
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 md:bottom-8 md:right-8 z-[99980] animate-slide-in-right pointer-events-auto max-w-[calc(100vw-2rem)] sm:max-w-[520px] w-full"
    >
      <div className="relative flex flex-col bg-[#363431] text-white rounded-xl shadow-2xl border-2 border-[#4a4743] overflow-visible">
        {/* Floating Top-Right Close Button */}
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute -top-3 -right-3 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#211f1e] hover:bg-[#2c2a28] text-stone-400 hover:text-white flex items-center justify-center border-2 border-[#4a4743] shadow-lg cursor-pointer z-20 transition-all hover:scale-110 active:scale-95"
          aria-label="Close and remind me later"
          title="Maybe Later"
        >
          <X size={16} className="stroke-[2.5]" />
        </button>

        {/* Main Banner Header / Summary */}
        <div className="flex flex-col sm:flex-row items-stretch">
          {/* Left Themed Icon Box */}
          <div className="w-full sm:w-28 bg-gradient-to-b from-[#2d2a28] to-[#211f1e] flex flex-row sm:flex-col items-center justify-center p-3.5 sm:p-2 shrink-0 rounded-t-xl sm:rounded-t-none sm:rounded-l-xl border-b sm:border-b-0 sm:border-r-2 border-[#4a4743] relative overflow-hidden">
            {/* Subtle Ambient Amber Glow */}
            <div className="absolute inset-0 bg-gradient-to-tr from-amber-500/25 via-orange-500/15 to-transparent pointer-events-none" />

            <div className="relative flex items-center justify-center">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shadow-inner">
                <Bell className="w-7 h-7 sm:w-8 sm:h-8 text-amber-400 drop-shadow-sm animate-bounce" />
              </div>
            </div>

            <span className="ml-3 sm:ml-0 sm:mt-1.5 text-[11px] font-black tracking-wider text-amber-300 uppercase drop-shadow z-10">
              ALERTS
            </span>
          </div>

          {/* Right Text Content */}
          <div className="px-4 sm:px-5 py-3.5 flex-1 min-w-0 flex flex-col justify-center">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
              <h3 className="font-black text-base sm:text-lg text-white leading-tight flex items-center gap-1.5">
                Stay Ahead of the Board!
              </h3>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] font-bold text-amber-300 bg-amber-500/20 px-2 py-0.5 rounded border border-amber-500/30 flex items-center gap-1">
                  <BellRing size={11} />
                  Live Push
                </span>
                <span className="text-[10px] font-bold text-stone-400 bg-black/30 px-2 py-0.5 rounded border border-stone-700">
                  REAL-TIME
                </span>
              </div>
            </div>

            <p className="text-xs sm:text-[13px] text-stone-300 font-medium leading-relaxed">
              Enable notifications to get real-time game challenges from friends, morning streak protection puzzles, and coach announcements.{' '}
              <Link 
                to="/notifications" 
                className="text-amber-400 hover:text-amber-300 underline font-semibold transition-colors"
                onClick={handleDismiss}
              >
                Learn more
              </Link>
            </p>
          </div>
        </div>

        {/* Granular Preference Accordion */}
        {showPreferences && (
          <div className="px-4 sm:px-5 py-3 border-t border-[#4a4743] bg-[#292725]/90 space-y-2.5 animate-fade-in">
            <div className="flex items-center justify-between pb-1 border-b border-stone-700/50">
              <span className="text-xs font-bold text-stone-300 uppercase tracking-wider">
                Notification Categories
              </span>
              <span className="text-[11px] text-stone-400">Custom Alerts</span>
            </div>

            {/* 1. Live Match Challenges */}
            <label className="flex items-start justify-between gap-3 p-2.5 rounded-lg bg-black/25 border border-stone-700/60 hover:bg-black/35 cursor-pointer transition-colors">
              <div className="flex items-start gap-2.5 min-w-0">
                <Swords size={16} className="text-amber-400 mt-0.5 shrink-0" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-white">Live Match Challenges</span>
                    <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                      Recommended
                    </span>
                  </div>
                  <p className="text-[11px] text-stone-400 mt-0.5 leading-snug">
                    Instant alerts when friends, academy rivals, or coaches invite you to a game.
                  </p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={alertPreferences.challenges}
                onChange={(e) => setAlertPreferences(prev => ({ ...prev, challenges: e.target.checked }))}
                className="w-4 h-4 rounded mt-1 accent-amber-500 cursor-pointer"
                aria-label="Toggle Live Match Challenges"
              />
            </label>

            {/* 2. Daily Tactics & Streak Protection */}
            <label className="flex items-start justify-between gap-3 p-2.5 rounded-lg bg-black/25 border border-stone-700/60 hover:bg-black/35 cursor-pointer transition-colors">
              <div className="flex items-start gap-2.5 min-w-0">
                <Sparkles size={16} className="text-blue-400 mt-0.5 shrink-0" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-white">Daily Tactics & Streak Alerts</span>
                  </div>
                  <p className="text-[11px] text-stone-400 mt-0.5 leading-snug">
                    Morning puzzle drops and timely reminders to maintain your daily chess streak.
                  </p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={alertPreferences.dailyPuzzles}
                onChange={(e) => setAlertPreferences(prev => ({ ...prev, dailyPuzzles: e.target.checked }))}
                className="w-4 h-4 rounded mt-1 accent-amber-500 cursor-pointer"
                aria-label="Toggle Daily Tactics Alerts"
              />
            </label>

            {/* 3. Tournaments & Coaching */}
            <label className="flex items-start justify-between gap-3 p-2.5 rounded-lg bg-black/25 border border-stone-700/60 hover:bg-black/35 cursor-pointer transition-colors">
              <div className="flex items-start gap-2.5 min-w-0">
                <Trophy size={16} className="text-purple-400 mt-0.5 shrink-0" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-white">Tournaments & Academy Coaching</span>
                  </div>
                  <p className="text-[11px] text-stone-400 mt-0.5 leading-snug">
                    Start calls for scheduled arena tournaments and reminders for batch classroom sessions.
                  </p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={alertPreferences.tournaments}
                onChange={(e) => setAlertPreferences(prev => ({ ...prev, tournaments: e.target.checked }))}
                className="w-4 h-4 rounded mt-1 accent-amber-500 cursor-pointer"
                aria-label="Toggle Tournaments and Coaching Alerts"
              />
            </label>
          </div>
        )}

        {/* Action Button Bar */}
        <div className="px-4 sm:px-5 py-3 bg-[#2a2826] rounded-b-xl border-t border-[#4a4743] flex flex-col sm:flex-row items-center justify-between gap-2.5">
          {/* Preferences Toggle Link */}
          <button
            type="button"
            onClick={() => setShowPreferences(!showPreferences)}
            className="text-xs text-stone-400 hover:text-stone-200 font-semibold flex items-center gap-1 transition-colors py-1 cursor-pointer order-2 sm:order-1"
          >
            {showPreferences ? (
              <>
                <ChevronUp size={15} />
                <span>Hide Options</span>
              </>
            ) : (
              <>
                <ChevronDown size={15} />
                <span>Customize Alerts</span>
              </>
            )}
          </button>

          {/* Decision Buttons */}
          <div className="flex items-center gap-2 w-full sm:w-auto justify-end order-1 sm:order-2">
            <KSButton
              color="stone"
              textColor="#e7e5e4"
              width="auto"
              height="42px"
              onClick={handleDismiss}
              title="Maybe Later"
              className="px-3 sm:px-4 text-xs font-bold flex-1 sm:flex-initial"
              disabled={subscribing}
            >
              Maybe Later
            </KSButton>
            <KSButton
              color="yellow"
              textColor="#1c1917"
              width="auto"
              height="42px"
              onClick={handleEnable}
              loading={subscribing}
              title="Enable browser notifications"
              className="px-4 text-xs font-black flex-1 sm:flex-initial"
            >
              <Check size={16} className="stroke-[3]" />
              <span>{showPreferences ? 'Save & Enable' : 'Enable Alerts'}</span>
            </KSButton>
          </div>
        </div>
      </div>
    </aside>
  );
}
